import type { FieldDef, Patient, Session } from "../types";
import { fmtCurrency, fmtDateLong, maskSsn } from "./format";

interface Args {
  patient: Patient;
  session: Session;
  ssn?: string | null; // only present when revealed
}

export function buildFieldDefs({ patient: p, session: s, ssn }: Args): Record<string, FieldDef> {
  const ssnDisplay = ssn ?? maskSsn(p.ssnLast4);
  return {
    fullName: {
      label: "Full legal name",
      value: `${p.firstName} ${p.middleName} ${p.lastName}`,
      group: "identity",
    },
    firstName: { label: "First name", value: p.firstName, group: "identity" },
    lastName: { label: "Last name", value: p.lastName, group: "identity" },
    middleName: { label: "Middle name", value: p.middleName, group: "identity" },
    dob: {
      label: "Date of birth",
      value: fmtDateLong(p.dob),
      copyValue: p.dob,
      hint: "MM/DD/YYYY in some portals",
      group: "identity",
    },
    ssn: {
      label: "Social Security Number",
      value: ssnDisplay,
      copyValue: ssn ?? "",
      sensitive: true,
      group: "identity",
    },
    ssnLast4: { label: "SSN last 4", value: p.ssnLast4, group: "identity" },
    phone: { label: "Mobile phone", value: p.phone, group: "contact" },
    email: { label: "Email", value: p.email, group: "contact" },
    addressLine: {
      label: "Street address",
      value: `${p.address.street}, ${p.address.city}, ${p.address.state} ${p.address.zip}`,
      group: "address",
    },
    addressStreet: { label: "Street", value: p.address.street, group: "address" },
    addressCity: { label: "City", value: p.address.city, group: "address" },
    addressState: { label: "State", value: p.address.state, group: "address" },
    addressZip: { label: "ZIP", value: p.address.zip, group: "address" },
    employer: { label: "Employer", value: p.employer, group: "employment" },
    employmentLength: {
      label: "Length of employment",
      value: p.employmentLength,
      group: "employment",
    },
    annualIncome: {
      label: "Annual gross income",
      value: fmtCurrency(p.annualIncome),
      copyValue: String(p.annualIncome),
      group: "employment",
    },
    monthlyHousing: {
      label: "Monthly housing payment",
      value: fmtCurrency(p.monthlyHousing),
      copyValue: String(p.monthlyHousing),
      group: "employment",
    },
    requestedAmount: {
      label: "Requested amount",
      value: fmtCurrency(s.requestedAmount),
      copyValue: String(s.requestedAmount),
      group: "treatment",
    },
    treatmentNotes: {
      label: "Treatment description",
      value: s.treatmentNotes,
      group: "treatment",
    },
  };
}
